import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Volume2, 
  Play, 
  Pause, 
  Square, 
  RefreshCw, 
  ArrowLeft, 
  Check, 
  AlertCircle, 
  Sparkles, 
  Settings,
  Sliders,
  Compass,
  MessageSquare
} from 'lucide-react';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { AdUnit } from './AdUnit';

export const TextToSpeech = () => {
  const [inputText, setInputText] = useState('Welcome to Toolmetrico Voice Studio! This state-of-the-art text-to-speech utility runs entirely client-side inside your browser thread. You can customize the voice, speed pitch, and adjust parameters to compile high fidelity artificial speech instantly.');
  const [voices, setVoices] = useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState('');
  const [rate, setRate] = useState(1.0); // 0.5 to 2.0
  const [pitch, setPitch] = useState(1.0); // 0.5 to 2.0
  const [volume, setVolume] = useState(1.0); // 0 to 1
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Word tracking index highlight states
  const [currentWordIdx, setCurrentWordIdx] = useState(-1);
  const wordsArray = inputText.split(/\s+/);

  const synthRef = useRef(window.speechSynthesis);
  const utteranceRef = useRef(null);

  // Load browser voices
  useEffect(() => {
    if (!synthRef.current) {
      setErrorMsg('Web Speech Synthesis API not supported on this browser engine.');
      return;
    }

    const loadVoices = () => {
      const availableVoices = synthRef.current.getVoices();
      setVoices(availableVoices);
      if (availableVoices.length > 0) {
        // Fallback or pick standard US voice
        const defaultVoice = availableVoices.find(v => v.lang.includes('US') || v.default) || availableVoices[0];
        setSelectedVoiceName(defaultVoice.name);
      }
    };

    loadVoices();
    if (synthRef.current.onvoiceschanged !== undefined) {
      synthRef.current.onvoiceschanged = loadVoices;
    }

    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  const handleSpeak = () => {
    if (!synthRef.current || !inputText.trim()) return;

    // Reset synthesis if already active
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(inputText);
    
    // Bind selected voice properties
    const chosenVoice = voices.find(v => v.name === selectedVoiceName);
    if (chosenVoice) {
      utterance.voice = chosenVoice;
    }

    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;

    // Track word-boundary events to highlight spoken word dynamically
    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        const charIdx = event.charIndex;
        // Count words before the character index to locate exact word sequence position
        const textUpToChar = inputText.substring(0, charIdx);
        const wordCount = textUpToChar.trim().split(/\s+/).length;
        setCurrentWordIdx(textUpToChar.trim() === '' ? 0 : wordCount);
      }
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setCurrentWordIdx(-1);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setCurrentWordIdx(-1);
    };

    utteranceRef.current = utterance;
    setIsSpeaking(true);
    setIsPaused(false);
    synthRef.current.speak(utterance);
  };

  const handlePauseResume = () => {
    if (!synthRef.current) return;

    if (isPaused) {
      synthRef.current.resume();
      setIsPaused(false);
    } else {
      synthRef.current.pause();
      setIsPaused(true);
    }
  };

  const handleStop = () => {
    if (!synthRef.current) return;
    synthRef.current.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
    setCurrentWordIdx(-1);
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <RouterLink to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </RouterLink>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>AI Speech Synthesis Studio</span>
      </div>


      {/* Main Studio Core Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="tts-studio-grid">
        
        {/* Left Column: Text inputs, Highlights and Player controllers */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', minWidth: 0 }}>
          
          {/* Text input area panel */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={labelStyle}>Script Text Inputs</span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700 }}>
                {inputText.length} Characters | {inputText.trim().split(/\s+/).filter(Boolean).length} Words
              </span>
            </div>

            {/* Dynamic word highlighting workspace box */}
            {isSpeaking ? (
              <div style={{
                width: '100%',
                height: '180px',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                padding: '18px',
                overflowY: 'auto',
                lineHeight: 1.6,
                fontSize: '1rem',
                color: 'var(--text-primary)',
                boxSizing: 'border-box'
              }}>
                {wordsArray.map((word, idx) => (
                  <span 
                    key={idx}
                    style={{
                      marginRight: '6px',
                      borderRadius: '4px',
                      padding: '2px 4px',
                      background: currentWordIdx === idx ? 'rgba(5, 117, 251, 0.15)' : 'transparent',
                      color: currentWordIdx === idx ? 'var(--primary)' : 'var(--text-primary)',
                      fontWeight: currentWordIdx === idx ? 800 : 400,
                      transition: 'background 0.08s ease'
                    }}
                  >
                    {word}
                  </span>
                ))}
              </div>
            ) : (
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type or paste scripts to synthesize into vocal outputs..."
                style={{
                  width: '100%',
                  height: '180px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  padding: '16px',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  outline: 'none',
                  resize: 'none',
                  boxSizing: 'border-box'
                }}
              />
            )}

            {/* Core Speech Player Controls drawer */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {!isSpeaking ? (
                <button
                  onClick={handleSpeak}
                  disabled={!inputText.trim()}
                  style={{
                    flex: '1 0 auto',
                    height: '46px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(90deg, var(--primary) 0%, var(--secondary) 100%)',
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'var(--transition-smooth)'
                  }}
                  className="speech-play-btn"
                >
                  <Play size={16} /> Synthesize & Play Speech
                </button>
              ) : (
                <>
                  <button
                    onClick={handlePauseResume}
                    style={{
                      flex: 1,
                      height: '46px',
                      borderRadius: '10px',
                      border: '1.5px solid var(--border-color)',
                      background: '#ffffff',
                      color: 'var(--text-primary)',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {isPaused ? <Play size={16} /> : <Pause size={16} />}
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    onClick={handleStop}
                    style={{
                      flex: 1,
                      height: '46px',
                      borderRadius: '10px',
                      border: 'none',
                      background: '#ef4444',
                      color: '#ffffff',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Square size={14} /> Stop
                  </button>
                </>
              )}
            </div>

          </div>

        </div>

        {/* Right Column: Voice configuration panels and DSP waveform */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', minWidth: 0 }} className="tts-preview-column">
          
          {/* Vocal calibrations parameters panel */}
          <div style={cardStyle}>
            <span style={labelStyle}>Vocal Parameters Calibrations</span>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Voice selector */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Choose Accent Vocalist Voice</label>
                <select
                  value={selectedVoiceName}
                  onChange={(e) => setSelectedVoiceName(e.target.value)}
                  style={selectStyle}
                >
                  {voices.map((v, i) => (
                    <option key={i} value={v.name}>
                      {v.name} ({v.lang})
                    </option>
                  ))}
                </select>
              </div>

              {/* Speed slider */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', fontWeight: 700 }}>
                  <span>Vocal speed rate</span>
                  <span>{rate}x</span>
                </div>
                <input 
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={rate}
                  onChange={(e) => setRate(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary)' }}
                />
              </div>

              {/* Pitch slider */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', fontWeight: 700 }}>
                  <span>Speech Pitch tone</span>
                  <span>{pitch}</span>
                </div>
                <input 
                  type="range"
                  min="0.5"
                  max="1.5"
                  step="0.1"
                  value={pitch}
                  onChange={(e) => setPitch(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary)' }}
                />
              </div>

              {/* Volume slider */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', fontWeight: 700 }}>
                  <span>Master volume Gain</span>
                  <span>{Math.round(volume * 100)}%</span>
                </div>
                <input 
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary)' }}
                />
              </div>

            </div>
          </div>

          {/* Wave visualizer spectrum display */}
          <div style={cardStyle}>
            <span style={labelStyle}>Live Audio wave outputs</span>
            
            <div style={{
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid var(--border-color)',
              aspectRatio: '16/8',
              background: '#090d16',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              position: 'relative'
            }}>
              {/* Pulsing visual bars linked to play speech thread */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                height: '50px',
                width: '100%',
                justifyContent: 'center'
              }}>
                {[30, 55, 15, 75, 95, 40, 85, 60, 20, 80, 45, 90, 35, 70, 15, 50, 25, 80, 10].map((h, i) => (
                  <div
                    key={i}
                    style={{
                      width: '3px',
                      height: isSpeaking && !isPaused ? `${h}%` : '6px',
                      background: 'linear-gradient(180deg, var(--primary) 0%, var(--secondary) 100%)',
                      borderRadius: '2px',
                      transition: isSpeaking && !isPaused ? 'height 0.1s ease-in-out' : 'height 0.3s'
                    }}
                  />
                ))}
              </div>

              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '12px',
                fontSize: '0.6rem',
                color: '#64748b',
                fontFamily: 'monospace'
              }}>
                {isSpeaking && !isPaused ? 'SYNTHESIZING SOUNDSTREAM' : 'IDLE'}
              </div>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tts-studio-grid {
            grid-template-columns: minmax(0, 1fr) 420px !important;
          }
        }

        .speech-play-btn:hover {
          opacity: 0.95;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(5, 117, 251, 0.15) !important;
        }
      `}</style>

    {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="text-to-speech" />
    </div>
  );
};

// CSS properties layouts
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const selectStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  fontSize: '0.9rem',
  fontWeight: 600,
  outline: 'none',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
  cursor: 'pointer'
};

export default TextToSpeech;
